import { cn } from '@/shared/utils/cn';
import { isKeyOf } from '@/shared/utils/isKeyOf';
import { useSortable } from '@dnd-kit/sortable';
import { Arguments, CSS } from '@dnd-kit/utilities';
import { CopyIcon, DeleteIcon, GrabIcon, PlusIcon } from '@schuettflix/icons-react';
import { DatePickerField, NumberField } from '@schuettflix/planum-react';
import { Combobox, ComboboxOption, TextField } from '@schuettflix/react-components';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Control, Controller, useController, UseFormRegister, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFulfillmentContext } from '../context/FulfillmentContext';
import { ALLOWED_MIME_TYPE } from '../context/FulfillmentUploadContext';
import { useFulfillmentSectionsErrors } from '../hooks/useFulfillmentSectionsErrors';
import { useIndividualLineItemProductOptions } from '../hooks/useIndividualLineItemProductOptions.tsx';
import { useSectionsValidity } from '../hooks/useSectionsValidity';
import { useUploadFiles } from '../hooks/useUploadFiles.tsx';
import {
    IndividualLineItem,
    IndividualLineItemSchema,
    PartialIndividualLineItemInput,
} from '../shared/IndividualLineItemSchema';
import { ProductFallbackDataMap } from '../types/ProductFallbackDataMap';
import { getFlattenedAndTranslatedZodErrorMessages } from '../utils/getFlattenedAndTranslatedZodErrorMessages';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';
import { getLimitedDateRanges } from '@/shared/utils/getLimitedDateRanges.ts';
import { formatDateTimezoneOffsetHotfix } from '@/tools/hotfix/formatDateTimezoneOffsetHotfix.ts';
import { isHazardousWasteProduct } from '@/shared/domain/product/isHazardousWasteProduct.ts';
import { WeighingNoteBillingWarning } from './WeighingNoteBillingWarning.tsx';
import { useDuplicateCheck } from '../hooks/useDuplicateCheck.tsx';
import { useDuplicateCheckContext } from '../context/DuplicateCheckContext.tsx';
import { asyncDelay } from '@/shared/utils/async.ts';

type IndividualLineItemFormProps = {
    id: string;
    control: Control<{ items: PartialIndividualLineItemInput[] }>;
    register: UseFormRegister<{ items: PartialIndividualLineItemInput[] }>;
    index: number;
    onRemove: () => void;
    disableActions?: boolean;
    productFallbackDataMap: ProductFallbackDataMap;
    forceValidation: boolean;
    onCopy: (lineItem: PartialIndividualLineItemInput) => void;
};
export const IndividualLineItemForm: React.FC<IndividualLineItemFormProps> = ({
    id,
    control,
    index,
    onRemove,
    disableActions,
    productFallbackDataMap,
    register,
    forceValidation,
    onCopy,
}) => {
    const { t } = useTranslation();
    const { isSubmitted, products, documents } = useFulfillmentContext();
    const uploadFiles = useUploadFiles();

    const {
        attributes: sortableAttributes,
        listeners: sortableListeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({
        id,
        disabled: disableActions,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    //////////////////////////////////////////
    // Data for inputs                      //
    //////////////////////////////////////////

    const fieldValues = useWatch({
        control,
        name: `items.${index}`,
    });

    const productNameOptions = useIndividualLineItemProductOptions(productFallbackDataMap);

    const documentUrlsOptions: ComboboxOption<string>[] = documents.map(document => ({
        value: document.url,
        label: document.fileName,
    }));
    const onDocumentUrlsFileInputChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            // check if files are present
            if (!event?.target?.files) return;

            // reset field value (so the user can upload the same file again)
            const files = [...event.target.files];
            event.target.value = '';

            return uploadFiles(files);
        },
        [uploadFiles]
    );

    const productUnit =
        products.find(product => product.id === fieldValues.productId)?.quote?.unit ||
        (fieldValues.productId && productFallbackDataMap[fieldValues.productId]?.unit);
    const unit = productUnit ? t(SUPPORTED_UNIT_LABEL[productUnit]) : '';

    const referenceDocumentPlaceholder = useMemo(() => {
        switch (productUnit) {
            case 'TON':
            case 'KILOGRAM':
            case 'CUBIC_METER':
                return t('product.lineItemListing.referenceDocumentWeighingProof');
            case 'HOUR':
            case 'MINUTE':
            case 'DAY':
                return t('product.lineItemListing.referenceDocumentTimeProof');
            default:
                return t('product.lineItemListing.referenceDocument');
        }
    }, [productUnit, t]);

    const isHazardousWaste = useMemo<boolean>(() => {
        const productType = products.find(product => product.id === fieldValues.productId)?.type;
        if (!productType) return false;
        return isHazardousWasteProduct(productType);
    }, [products, fieldValues.productId]);

    const ErrorMessagesSchema = useMemo(
        () =>
            IndividualLineItemSchema.omit({ accompanyingDocumentNumber: true }).extend({
                accompanyingDocumentNumber: isHazardousWaste
                    ? IndividualLineItemSchema.shape.accompanyingDocumentNumber.unwrap()
                    : IndividualLineItemSchema.shape.accompanyingDocumentNumber,
            }),
        [isHazardousWaste]
    );

    const errorTranslationMap = useMemo<
        Arguments<typeof getFlattenedAndTranslatedZodErrorMessages>[0]['errorTranslationMap']
    >(
        () => ({
            productId: t('product.fulfillment.errorMessages.productId.required'),
            amount: {
                invalid_type: t('product.fulfillment.errorMessages.amount.required'),
                too_small: t('product.fulfillment.errorMessages.amount.minValue'),
            },
            serviceDate: t('product.fulfillment.errorMessages.serviceDate.invalid'),
            accompanyingDocumentNumber: t('product.fulfillment.errorMessages.accompanyingDocumentNumber.required'),
        }),
        [t]
    );

    //////////////////////////////////////////
    // Validation                           //
    //////////////////////////////////////////

    const checkForInternalErrors = Object.values(fieldValues).some(v => !!v) || forceValidation;
    const internalErrors = checkForInternalErrors
        ? (getFlattenedAndTranslatedZodErrorMessages({
              schema: ErrorMessagesSchema,
              input: fieldValues,
              errorTranslationMap,
              shouldUseIssueCodeAsFallback: true,
          })?.fieldErrors ?? null)
        : null;

    const visibleErrors = isSubmitted ? internalErrors : null;

    type FieldName = keyof IndividualLineItem;
    const scrollToField = useCallback(
        (fieldNameOrElement: FieldName | HTMLElement | null) => {
            if (!fieldNameOrElement) return;

            if (typeof fieldNameOrElement === 'string')
                return document
                    .querySelector(`[name="items.${index}.${fieldNameOrElement}"]`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

            fieldNameOrElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        [index]
    );

    const productIdRef = useRef<HTMLInputElement>(null);
    const amountRef = useRef<HTMLInputElement>(null);
    const serviceDateRef = useRef<HTMLDivElement>(null);
    const documentUrlsRef = useRef<HTMLInputElement>(null);
    const scrollToElementMap: Record<FieldName, () => void> = {
        productId: () => scrollToField(productIdRef.current),
        amount: () => scrollToField(amountRef.current),
        serviceDate: () => scrollToField(serviceDateRef.current),
        referenceDocument: () => scrollToField('referenceDocument'),
        licensePlate: () => scrollToField('licensePlate'),
        accompanyingDocumentNumber: () => scrollToField('accompanyingDocumentNumber'),
        documentUrls: () => scrollToField(documentUrlsRef.current),
    };
    useFulfillmentSectionsErrors(
        internalErrors
            ? (
                  Object.entries(internalErrors).filter(([_, value]) => value !== undefined) as [FieldName, string[]][]
              ).flatMap(([key, value]) => {
                  if (!isKeyOf(internalErrors, key)) return [];
                  return {
                      field: `lineItems.${index}.${key}`,
                      message: value[0],
                      scrollToElement: scrollToElementMap[key],
                  };
              })
            : []
    );
    useSectionsValidity(`items.${index}`, !internalErrors);

    //////////////////////////////////////////
    // Duplicate Check                      //
    //////////////////////////////////////////

    const { duplicateCheckState, runDuplicateCheck } = useDuplicateCheck({ individualLineItem: fieldValues, index });

    //////////////////////////////////////////
    // Side effects                         //
    //////////////////////////////////////////

    // rerun duplicate check on unit or partner change
    const { productDataMap } = useDuplicateCheckContext();
    const currentPartnerId = fieldValues.productId && productDataMap[fieldValues.productId]?.partnerId;
    useEffect(() => {
        runDuplicateCheck();
        // we don't want to run the duplicate check when runDuplicateCheck changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productUnit, currentPartnerId]);

    // Ensure we have valid product ids, empty invalid ones
    const {
        field: { onChange: setProductId },
    } = useController({ name: `items.${index}.productId`, control });
    useEffect(() => {
        if (fieldValues.productId && !products.find(product => product.id === fieldValues.productId)) {
            setProductId(undefined);
        }
    }, [fieldValues, products, setProductId]);

    // Ensure we have valid document urls, empty invalid ones
    const {
        field: { onChange: setDocumentUrls },
    } = useController({ name: `items.${index}.documentUrls`, control });
    useEffect(() => {
        if (fieldValues.documentUrls && !documents.find(document => document.url === fieldValues.documentUrls?.[0])) {
            setDocumentUrls(undefined);
        }
    }, [fieldValues, documents, setDocumentUrls]);

    const { minDate, maxDate } = getLimitedDateRanges();

    return (
        <li
            ref={setNodeRef}
            style={style}
            className="w-full animate-[pulse.6s_ease-in-out] border-b pb-4 last:border-b-0 last:pb-0"
        >
            <div className="flex gap-1">
                <div className="grid grid-cols-12 gap-x-2 gap-y-1">
                    <Controller
                        name={`items.${index}.productId`}
                        control={control}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <div className="col-span-4 2xl:col-span-2">
                                <Combobox
                                    {...field}
                                    onBlur={runDuplicateCheck}
                                    options={productNameOptions}
                                    value={field.value ?? null}
                                    placeholder={t('product.lineItemListing.productName')}
                                    variant="sm"
                                    errorMessage={visibleErrors?.productId?.[0]}
                                />
                                <span ref={productIdRef} />
                            </div>
                        )}
                    />
                    <Controller
                        name={`items.${index}.amount`}
                        control={control}
                        render={({ field: { onChange, ...field } }) => (
                            <NumberField
                                {...field}
                                size="lg"
                                value={field.value}
                                step={0.001}
                                placeholder={t('product.lineItemListing.amount')}
                                aria-label={t('product.lineItemListing.amount')}
                                trailingSlot={unit}
                                onValueChange={onChange}
                                onBlur={async () => {
                                    await asyncDelay(0);
                                    runDuplicateCheck();
                                }}
                                error={visibleErrors?.amount?.[0]}
                                className="col-span-2 min-h-12 w-full 2xl:col-span-1"
                                ref={amountRef}
                            />
                        )}
                    />
                    <TextField
                        {...register(`items.${index}.referenceDocument`)}
                        onBlur={runDuplicateCheck}
                        placeholder={referenceDocumentPlaceholder}
                        errorMessage={visibleErrors?.referenceDocument?.[0]}
                        variant="sm"
                        onKeyDown={e => {
                            if (e.key === ',' || e.key === '+' || e.key === ' ') {
                                e.preventDefault();
                            }
                        }}
                        className="col-span-4 xl:col-span-2"
                    />
                    <TextField
                        {...register(`items.${index}.licensePlate`)}
                        placeholder={t('product.lineItemListing.licensePlate')}
                        errorMessage={visibleErrors?.licensePlate?.[0]}
                        variant="sm"
                        className="col-span-2 2xl:col-span-1"
                    />
                    <div className="col-span-2" ref={serviceDateRef}>
                        <Controller
                            name={`items.${index}.serviceDate`}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <DatePickerField
                                    mode="single"
                                    size="lg"
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    selected={value ? new Date(value) : undefined}
                                    onSelect={date => {
                                        const value = date ? formatDateTimezoneOffsetHotfix(date) : undefined;
                                        onChange(value);
                                    }}
                                    onDayBlur={runDuplicateCheck}
                                    placeholder={t('product.lineItemListing.serviceDate')}
                                    error={visibleErrors?.serviceDate?.[0]}
                                />
                            )}
                        />
                    </div>
                    <Controller
                        name={`items.${index}.documentUrls`}
                        control={control}
                        render={({ field: { ref: _ref, onChange, value, ...field } }) => (
                            <div className="col-span-2">
                                <Combobox
                                    {...field}
                                    options={documentUrlsOptions}
                                    value={value?.[0] ?? null}
                                    onChange={value => (value ? onChange([value]) : onChange(undefined))}
                                    placeholder={t('product.lineItemListing.documentUrls')}
                                    variant="sm"
                                    errorMessage={visibleErrors?.documentUrls?.[0]}
                                    renderActionButton={() => (
                                        <>
                                            <PlusIcon />
                                            {t('product.lineItemListing.uploadDocumentLabel')}
                                        </>
                                    )}
                                    onActionButton={() => documentUrlsRef.current?.click()}
                                    allowDeselect
                                />
                                <input
                                    type="file"
                                    accept={ALLOWED_MIME_TYPE}
                                    hidden
                                    ref={documentUrlsRef}
                                    onChange={event =>
                                        onDocumentUrlsFileInputChange(event).then(
                                            successfulUploads =>
                                                successfulUploads?.[0]?.remotePath &&
                                                onChange([successfulUploads[0].remotePath])
                                        )
                                    }
                                />
                            </div>
                        )}
                    />
                    {isHazardousWaste && (
                        <TextField
                            {...register(`items.${index}.accompanyingDocumentNumber`)}
                            placeholder={t('product.lineItemListing.accompanyingDocumentNumber')}
                            errorMessage={visibleErrors?.accompanyingDocumentNumber?.[0]}
                            variant="sm"
                            className="col-span-4 2xl:col-span-2"
                        />
                    )}
                </div>
                <div className="flex items-center justify-end gap-4">
                    <GrabIcon
                        {...sortableAttributes}
                        {...sortableListeners}
                        disabled={disableActions}
                        role={disableActions ? 'none' : 'button'}
                        className="focus-within:outline-none"
                    />
                    <CopyIcon
                        className={cn(!disableActions && 'cursor-pointer')}
                        disabled={disableActions}
                        onClick={() => {
                            if (disableActions) {
                                return;
                            }
                            const { amount: _, referenceDocument: __, ...values } = fieldValues;
                            onCopy(values);
                        }}
                    />
                    <DeleteIcon
                        className={cn(!disableActions && 'cursor-pointer')}
                        disabled={disableActions}
                        onClick={() => !disableActions && onRemove()}
                    />
                </div>
            </div>

            {duplicateCheckState && (
                <span className="font-copy-sm text-critical px-2 pt-2">{duplicateCheckState.message}</span>
            )}

            {!!fieldValues.referenceDocument && !!fieldValues.amount && !!fieldValues.serviceDate && (
                <WeighingNoteBillingWarning
                    weighingNoteNumber={fieldValues.referenceDocument ?? ''}
                    netWeight={fieldValues.amount ?? 0}
                    weightUnit={productUnit === 'TON' ? 't' : 'kg'}
                    serviceDate={
                        fieldValues.serviceDate ? formatDateTimezoneOffsetHotfix(new Date(fieldValues.serviceDate)) : ''
                    }
                    licensePlate={fieldValues.licensePlate !== '' ? fieldValues.licensePlate : undefined}
                />
            )}
        </li>
    );
};
